import { useEffect } from 'react';
import { App as CapacitorApp, type URLOpenListenerEvent } from '@capacitor/app';
import { Browser } from '@capacitor/browser';

import { useExecMutationFetch } from '@/shared/apiClient/useExecMutationFetch';
import { useToastStore } from '@/shared/store/toastStore';
import { linkTestgate as linkTestgateApi, TESTGATE_CONNECT_CALLBACK_URL } from '@/shared/auth/testgateConnect';

/**
 * TestGate 연결 딥링크 수신기 — openTestgateConnect()로 연 인앱 브라우저가 로그인을 마치면
 * TESTGATE_CONNECT_CALLBACK_URL(com.gate.erp://testgate-connect)로 리다이렉트하는데, OS가 이걸
 * 'appUrlOpen'으로 앱에 넘겨준다. 연결 도중 앱이 백그라운드로 내려갔다 이 딥링크로 복귀할 수 있어서
 * Home 화면이 아니라 App.tsx에서 앱 생명주기 내내 등록해둔다.
 */
export function useTestgateConnectListener() {
  const showToast = useToastStore((s) => s.showToast);

  const { mutate: linkTestgate } = useExecMutationFetch({
    mutationFn: (testgateAccessToken: string) => linkTestgateApi(testgateAccessToken),
    // TODO: gate-erp의 student/parent home 화면이 아직 데이터 조회를 구현하기 전이라 무효화 대상 쿼리키가 없음.
    // 나중에 실제 home 쿼리를 붙일 때 이 키들과 맞춰주면 됨 (capacitor-gate-erp의 ['home'] 컨벤션 참고).
    invalidateKeys: [['home'], ['studentProfile']],
    withLoadingOverlay: false,
    successHandler: () => showToast('success', 'Test GATE+ 계정이 연결됐어요.', '5rem'),
    failHandler: () => showToast('error', 'Test GATE+ 연결에 실패했어요. 다시 시도해주세요.', '5rem'),
  });

  useEffect(() => {
    const handle = CapacitorApp.addListener('appUrlOpen', ({ url }: URLOpenListenerEvent) => {
      if (!url.startsWith(TESTGATE_CONNECT_CALLBACK_URL)) return;

      Browser.close().catch(() => {});

      const token = new URL(url).searchParams.get('accessToken');
      if (!token) return;

      linkTestgate(token);
    });

    return () => {
      handle.then((h) => h.remove());
    };
  }, [linkTestgate]);
}
